import { z } from 'zod';
import {
  VERSIONED_DEFAULT_SORT,
  renameRequestSchemaFor,
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionedCardShape,
  versionedContentSchema,
  versionedDetailSchema,
  versionedFiltersShape,
  versionedSortSchema,
} from './versioning.js';

/**
 * A criterion: what the scribe must RECOGNISE in what the person says
 * (refonte modulaire §6).
 *
 * It is the first consumer of the common versioning pattern (§4), and the
 * simplest of the four objects that share it: a name, one AI direction, and one
 * list of positive examples. No counter-examples, no labels on the examples,
 * and no reference of its own - which is why its reference extractor returns an
 * empty list and its « utilisé dans » stays empty until a protocol cites it.
 */

/** H10 (§16). Bounds of INPUT, not of meaning: an operator hits them by mistake, never by intent. */
export const CRITERION_NAME_MAX = 120;
export const CRITERION_DIRECTION_MAX = 4000;
export const CRITERION_EXAMPLES_MAX = 30;
export const CRITERION_EXAMPLE_MAX = 600;

/**
 * One positive example: an extract of an answer that satisfies the criterion,
 * without a label and without a comment (§6).
 *
 * Trimmed then refused when empty, and that order matters: a field holding
 * three spaces is an example an operator believes they wrote, and it would be
 * served to the scribe as a blank line.
 */
export const criterionExampleSchema = z.string().trim().min(1).max(CRITERION_EXAMPLE_MAX);

/* ---------------------------------------------------------------- notation */

/**
 * H44 (§16). The bounds of the OPTIONAL scoring, and they are bounds of input
 * like H10's: an operator hits them by mistake, never by intent.
 */
export const CRITERION_SCORING_BANDS_MIN = 2;
export const CRITERION_SCORING_BANDS_MAX = 10;
export const CRITERION_SCORING_INSTRUCTION_MAX = 2000;
export const CRITERION_BAND_TEXT_MAX = 1000;

/**
 * The top of the scale (§6), and the one number this file spells rather than
 * bounds: the last band ends exactly there, and it is the only band that
 * INCLUDES its upper bound.
 */
export const CRITERION_SCORING_TOP = 100;

/**
 * One band of the scale: an upper bound that is typed, and a description that
 * is required (§6).
 *
 * **The lower bound is not here**, and that is the decision of the grill of
 * 2026-09-16 rather than an omission: it is the previous band's upper, 0 for
 * the first. Stored, it would be a second copy of a number the neighbour
 * already carries - two places to keep in step, and a scale that can be stored
 * with a hole in it.
 */
export const criterionBandSchema = z.object({
  upper: z.number().int().min(1).max(CRITERION_SCORING_TOP),
  text: z.string().trim().min(1).max(CRITERION_BAND_TEXT_MAX),
});
export type CriterionBand = z.infer<typeof criterionBandSchema>;

/**
 * The scoring of a criterion, when it has one (§6, grill of 2026-09-16).
 *
 * Without it a criterion is BINARY - the scribe returns a verdict - and with it
 * the scribe returns a score from 0 to 100 that these bands tell how to read.
 * Which is why `scoring` is nullable on the content and never an empty object:
 * « pas de notation » is a state of the criterion, not a scoring with no band.
 *
 * The three refinements are what makes a list of bands a SCALE. They are
 * re-implemented as pure problems in `criterion/scoring.ts`, which is what the
 * editor greys its button on; `tests/criterion.test.ts` holds that the two
 * cannot disagree, because a draft the editor accepts and the server refuses is
 * a save that fails with nothing on the screen to repair.
 */
export const criterionScoringSchema = z
  .object({
    instruction: z.string().trim().max(CRITERION_SCORING_INSTRUCTION_MAX).default(''),
    bands: z
      .array(criterionBandSchema)
      .min(CRITERION_SCORING_BANDS_MIN)
      .max(CRITERION_SCORING_BANDS_MAX),
    /** The INDEX of the band from which the criterion is filled (§6, H45). */
    plancher: z.number().int().nonnegative(),
    /**
     * The INDEX of the band from which an answer is « passable » (§6, issue
     * #110), or null: below it a score reads in red, from it up to where
     * « rempli » begins in yellow - and at the END of a step, an objective
     * none of whose criteria falls under it is validated at that bar (§7.3,
     * issue #112). An index and not a number, for the reason
     * the plancher is one - the two marks live in the same version as the
     * bands they name, so a scale edited later carries its own marks along.
     * Defaulted at read time (§4.1): every scale written before #110 has none.
     */
    passable: z.number().int().nonnegative().nullable().default(null),
  })
  .superRefine((scoring, ctx) => {
    scoring.bands.forEach((band, index) => {
      // `noUncheckedIndexedAccess`: the neighbour is typed possibly absent, and
      // 0 is the right answer for the first band anyway - it is the floor of
      // the scale, and the comparison below is the same for every band.
      const lower = index === 0 ? 0 : (scoring.bands[index - 1]?.upper ?? 0);
      if (band.upper <= lower) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bands', index, 'upper'],
          message: 'upper bounds must strictly increase',
        });
      }
    });

    const last = scoring.bands[scoring.bands.length - 1];
    if (last !== undefined && last.upper !== CRITERION_SCORING_TOP) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bands', scoring.bands.length - 1, 'upper'],
        message: `the last band must end at ${CRITERION_SCORING_TOP}`,
      });
    }

    if (scoring.plancher >= scoring.bands.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plancher'],
        message: 'plancher must be the index of an existing band',
      });
    }

    // Strictly below, and that also keeps it on an existing band: the plancher
    // is one. A passable band AT the plancher would name a yellow zone of no
    // width, and one above it a zone where « passable » outranks « rempli ».
    if (scoring.passable !== null && scoring.passable >= scoring.plancher) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['passable'],
        message: 'the passable band must sit strictly below the plancher',
      });
    }
  });
export type CriterionScoring = z.infer<typeof criterionScoringSchema>;

/**
 * The scoring as somebody is TYPING it, and the one schema an autosave is
 * parsed by.
 *
 * A band added by « Ajouter une tranche » has no upper bound and no text yet -
 * that is what it is for - so the complete schema would answer 400 to the
 * autosave that fires two seconds later. The caps still hold, for the reason
 * `criterionDraftContentSchema` gives about the direction: a 3000-character
 * description could never become a version, so refusing it early costs
 * nothing. The three refinements do NOT hold, and that is the whole difference:
 * a scale half typed is not a scale, and it still has to survive a reload.
 */
export const criterionScoringDraftSchema = z.object({
  instruction: z.string().trim().max(CRITERION_SCORING_INSTRUCTION_MAX).default(''),
  bands: z
    .array(
      z.object({
        /*
          Down to 0 where a version's band starts at 1, and that single digit is
          deliberate: `0` is what a number field holds for the half-second
          between « 0 » and « 05 », and refusing it would make the autosave
          answer 400 on a keystroke. It costs nothing to allow - `scoringProblems`
          calls it non-increasing whatever its neighbour is, since no lower
          bound is below 0 - and `criterionBandSchema` still refuses it.
        */
        upper: z.number().int().min(0).max(CRITERION_SCORING_TOP).nullable(),
        text: z.string().trim().max(CRITERION_BAND_TEXT_MAX),
      }),
    )
    .min(CRITERION_SCORING_BANDS_MIN)
    .max(CRITERION_SCORING_BANDS_MAX),
  plancher: z.number().int().nonnegative(),
  passable: z.number().int().nonnegative().nullable().default(null),
});
export type CriterionScoringDraft = z.infer<typeof criterionScoringDraftSchema>;
export type CriterionBandDraft = CriterionScoringDraft['bands'][number];

/**
 * The CONTENT of one version - the totality of the criterion at that version,
 * name included (§4.1).
 *
 * **Deliberately not `.strict()`**, and this is the doctrine of §4.1 rather
 * than an omission: this schema parses rows written by earlier versions of the
 * code, and a strict object refuses a row the day a field is added. A new field
 * arrives `optional` with a default at read time; an unknown one is dropped
 * rather than crashing a screen on a base nobody can migrate backwards.
 *
 * A criterion with no example stays publishable: it is judged on its direction
 * alone, so `examples` defaults to the empty list and is never `null`.
 *
 * `scoring` is the field that proves the doctrine rather than illustrating it:
 * it arrives with this lot, so every criterion written before it is a row
 * without the key - hence the default at READ time, and hence `null` meaning
 * « ce critère est binaire » rather than « ce champ manque ».
 */
export const criterionContentSchema = z.object({
  name: z.string().trim().min(1).max(CRITERION_NAME_MAX),
  direction: z.string().trim().min(1).max(CRITERION_DIRECTION_MAX),
  examples: z.array(criterionExampleSchema).max(CRITERION_EXAMPLES_MAX).default([]),
  scoring: criterionScoringSchema.nullable().default(null),
});
export type CriterionContent = z.infer<typeof criterionContentSchema>;

/**
 * The content of a DRAFT: the same fields, each optional.
 *
 * A draft is what somebody is in the middle of typing, so a half-written
 * criterion has to survive a reload - and `min(1)` means an empty field is
 * spelled by the key being ABSENT rather than by an empty string. The bounds
 * still apply to what IS there: a 5000-character direction is refused as a
 * draft too, because it could never become a version.
 *
 * It is what the editor SENDS and what the editor READS BACK: one schema for
 * both halves of the round trip, because a draft served under the complete
 * schema is a draft the editor cannot parse on the very reload it protects.
 *
 * `scoring` is REPLACED and not just made optional: `.partial()` loosens
 * whether a key is there, never what is under it, so the three refinements of
 * `criterionScoringSchema` would still have refused a scale half typed - which
 * is the only state an autosave of a scoring ever fires in.
 */
export const criterionDraftContentSchema = criterionContentSchema.partial().extend({
  scoring: criterionScoringDraftSchema.nullable().optional(),
});
export type CriterionDraftContent = z.infer<typeof criterionDraftContentSchema>;

/**
 * What ONE pointed version says about its notation, in the two numbers a
 * reader needs without opening the version (§6, §7.2).
 *
 * The band COUNT and the plancher, and neither the bands nor their texts: the
 * card writes « noté · 5 tranches » and the objective's row writes « seuil :
 * plancher 50 », and a list of thirty criteria carrying thirty scales would
 * ship the whole library's prose to draw two numbers. The rail of the slider
 * needs the bands themselves and reads the pointed VERSION for them, once, when
 * the panel opens.
 */
export const criterionTagScoringSchema = z
  .object({
    bandCount: z.number().int().min(CRITERION_SCORING_BANDS_MIN),
    /** Where « rempli » begins with no threshold posed: the plancher band's lower bound. */
    plancher: z.number().int().min(0).max(CRITERION_SCORING_TOP),
  })
  .strict();
export type CriterionTagScoring = z.infer<typeof criterionTagScoringSchema>;

/**
 * One card of the list (§4, §6): the thirteen common fields of
 * `versionedCardShape`, plus the two facets that belong to a criterion.
 *
 * The spread is the pattern made visible: everything a card says that does not
 * depend on what the object means - the two pointers, the usage count, the
 * stamps, the three computed booleans - is declared once in `versioning.ts`,
 * and a criterion adds « combien d'exemples nourrissent le scribe » and what
 * its notation says. A persona adds its own facets the same way.
 *
 * ## `scoring` is read PER TAG, and that is not symmetry for its own sake
 *
 * The list of §6 shows the development version, and « noté · 5 tranches » is
 * read off `scoring.development`. What needs the other half is the protocol
 * editor (§7.2): an objective cites `{ id, tag }`, and whether a threshold may
 * be posed - and where the plancher sits when none is - is a fact about the
 * version THAT CITATION points at. A card carrying the development answer alone
 * would offer « + seuil » on a criterion scored in development and bare in
 * production, which is precisely the citation `threshold-without-scoring`
 * refuses.
 *
 * Either half is `null` on a version that poses no notation, and `production`
 * is `null` too when nothing was ever passed to production (§4). Null and never
 * a zero: §6 makes the mention appear « seulement quand le critère est noté »,
 * an absence does not display, and a zero would be a count the eye reads as one.
 */
export const criterionCardSchema = z
  .object({
    ...versionedCardShape,
    exampleCount: z.number().int().nonnegative(),
    scoring: z
      .object({
        production: criterionTagScoringSchema.nullable(),
        development: criterionTagScoringSchema.nullable(),
      })
      .strict(),
  })
  .strict();
export type CriterionCard = z.infer<typeof criterionCardSchema>;

/**
 * The list, served WHOLE - retired entries included - and filtered in the page.
 *
 * The decision, taken in this lot and worth its sentence: the bibliothèque is a
 * handful of criteria, « utilisé dans » is computed at read time anyway, and
 * the filter set of §4 is nine dimensions deep. Nine query parameters against
 * one small table would buy a round trip per keystroke and cost a pure module
 * that can be tested without a base. So the route reads, the page filters, and
 * `criterion-filters` holds the whole rule set.
 */
export const criterionListSchema = z
  .object({
    criteria: z.array(criterionCardSchema),
  })
  .strict();
export type CriterionList = z.infer<typeof criterionListSchema>;

/** Everything the editor reads in one call. */
export const criterionDetailSchema = versionedDetailSchema(
  criterionContentSchema,
  criterionDraftContentSchema,
);
export type CriterionDetail = z.infer<typeof criterionDetailSchema>;

/** One version read in full, for « Voir ». */
export const criterionVersionSchema = versionedContentSchema(criterionContentSchema);
export type CriterionVersion = z.infer<typeof criterionVersionSchema>;

/** « Enregistrer une nouvelle version », with its optional note and its production checkbox. */
export const criterionSaveVersionSchema = saveVersionRequestSchema(criterionContentSchema);
export type CriterionSaveVersion = z.infer<typeof criterionSaveVersionSchema>;

/** An autosave of the draft, on the partial content of `criterionDraftContentSchema`. */
export const criterionSaveDraftSchema = saveDraftRequestSchema(criterionDraftContentSchema);
export type CriterionSaveDraft = z.infer<typeof criterionSaveDraftSchema>;

/**
 * Renaming, on the criterion's OWN name field.
 *
 * The same 120 as the layer's default, so nothing moves for this object -
 * and that is exactly why the trap it closes was invisible here. A rename
 * writes its name into the server draft, which is read back through
 * `criterionDraftContentSchema`: built from that schema's own field, the two
 * cannot disagree whatever H10 does to the bound later.
 */
export const criterionRenameSchema = renameRequestSchemaFor(criterionContentSchema.shape.name);
export type CriterionRename = z.infer<typeof criterionRenameSchema>;

/* ------------------------------------------------------------------ filters */

/** The criterion's OWN filter (§6): how many examples feed the scribe. */
export const criterionExamplesFilterSchema = z.enum(['all', 'none', 'few', 'many']);
export type CriterionExamplesFilter = z.infer<typeof criterionExamplesFilterSchema>;

/** Where `few` stops and `many` starts, as read on the mockup: « 1 à 4 » and « 5 et plus ». */
export const CRITERION_FEW_EXAMPLES_MAX = 4;

/**
 * The criterion's SECOND own filter (§6): scored, not scored, or both.
 *
 * It does not join the sort, and that is written in §6 rather than left out:
 * « la notation (toutes, notés, non notés), dans Plus de filtres, qui ne trie
 * pas ». A scoring is not a quantity - two bands and ten bands are two shapes
 * of the same decision - so ordering a library by it would rank criteria on a
 * number that means nothing next to a neighbour's.
 */
export const criterionScoringFilterSchema = z.enum(['all', 'scored', 'unscored']);
export type CriterionScoringFilter = z.infer<typeof criterionScoringFilterSchema>;

/**
 * The four common sorts of §4 plus the criterion's own.
 *
 * Spread from `versionedSortSchema` so the four cannot drift, and `as const`
 * because `z.enum` needs a tuple: a plain spread widens to `string[]`, which
 * would make every option a bare `string` and lose the narrowing every label
 * table depends on.
 */
export const criterionSortSchema = z.enum([...versionedSortSchema.options, 'examples'] as const);
export type CriterionSort = z.infer<typeof criterionSortSchema>;

/** The default of the list: by name (§4, issue #113). */
export const CRITERION_DEFAULT_SORT: CriterionSort = VERSIONED_DEFAULT_SORT;

/**
 * The nine filters of §4 plus the sort, as ONE object.
 *
 * Thirteen of them are the common dimensions of `versionedFiltersShape`, spread
 * in; the criterion adds « exemples » and « notation », and widens the sort on
 * the first of the two only. It is the shape of
 * the page's URL state as much as the input of the pure module: a filtered list
 * has to be collable in a team thread, the way `?session=` already is. Every
 * field is optional with a neutral default, so an absent parameter and a reset
 * filter are the same thing - the alternative was `all` written nine times in a
 * URL nobody could read.
 */
export const criterionFiltersSchema = z
  .object({
    ...versionedFiltersShape,
    examples: criterionExamplesFilterSchema.default('all'),
    scoring: criterionScoringFilterSchema.default('all'),
    sort: criterionSortSchema.default(CRITERION_DEFAULT_SORT),
  })
  .strict();
export type CriterionFilters = z.infer<typeof criterionFiltersSchema>;

/** The neutral state: every criterion, retired excluded, sorted by name. */
export const CRITERION_FILTERS_DEFAULT: CriterionFilters = criterionFiltersSchema.parse({});
